/**
 * The dashboard's charts, drawn the desk's way: neutral marks (--mark) with the one value the
 * reader is looking for in the accent (--mark-hi), hairline grids, thin lines, values in text
 * colours, never the series colour. Every chart sits in a ChartCard that also offers the same
 * numbers as a table, so no value is reachable only by hovering.
 *
 * Props in, nothing fetched. Amounts arrive formatted by the caller's `format`.
 */
import type { Point } from "@tlcrm/shared";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { dayLabel, niceTicks, percent } from "../lib/chart";

/* ---- Measuring ----------------------------------------------------------------------------- */

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/* ---- Line ---------------------------------------------------------------------------------- */

const PAD = { top: 12, right: 16, bottom: 26, left: 48 };

/**
 * One series over time. A 2px line on a 10% wash, the last point marked and labelled, a
 * crosshair and tooltip on hover or arrow keys.
 */
export function LineChart({
  points,
  format,
  axisFormat = format,
  label = dayLabel,
  height = 200,
  ariaLabel,
}: {
  points: Point[];
  format: (n: number) => string;
  axisFormat?: (n: number) => string;
  label?: (iso: string) => string;
  height?: number;
  ariaLabel: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(0, ...points.map((p) => p.value));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const x = (i: number) =>
    PAD.left + (points.length <= 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (top ? (v / top) * plotH : 0);
  const path = points
    .map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join("");
  const area = points.length
    ? `${path}L${x(points.length - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`
    : "";
  // About six date labels across, whatever the width.
  const every = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotW / 90))));
  const last = points.length - 1;
  const shown = hover ?? null;

  const pick = (clientX: number, rect: DOMRect) => {
    if (!points.length) return;
    const rel = (clientX - rect.left - PAD.left) / (plotW || 1);
    setHover(Math.min(last, Math.max(0, Math.round(rel * last))));
  };

  return (
    <div className="chart" ref={ref} style={{ height }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          onMouseMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
          onMouseLeave={() => setHover(null)}
          onBlur={() => setHover(null)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? last + 1) - 1));
            if (e.key === "ArrowRight") setHover((h) => Math.min(last, (h ?? -1) + 1));
          }}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                className="chart-grid"
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
              />
              <text className="chart-tick" x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {axisFormat(t)}
              </text>
            </g>
          ))}
          {points.map((p, i) =>
            i % every === 0 || i === last ? (
              <text
                key={p.date}
                className="chart-tick"
                x={x(i)}
                y={height - 8}
                textAnchor={i === 0 ? "start" : i === last ? "end" : "middle"}
              >
                {i % every === 0 && last - i < every / 2 && i !== last ? "" : label(p.date)}
              </text>
            ) : null,
          )}
          <path className="chart-area" d={area} />
          <path className="chart-line" d={path} />
          {last >= 0 && (
            <>
              <circle className="chart-dot" cx={x(last)} cy={y(points[last]!.value)} r={4} />
              {shown === null && (
                <text
                  className="chart-value"
                  x={x(last) - 8}
                  y={y(points[last]!.value) - 10}
                  textAnchor="end"
                >
                  {format(points[last]!.value)}
                </text>
              )}
            </>
          )}
          {shown !== null && points[shown] && (
            <>
              <line
                className="chart-cross"
                x1={x(shown)}
                x2={x(shown)}
                y1={PAD.top}
                y2={PAD.top + plotH}
              />
              <circle className="chart-dot" cx={x(shown)} cy={y(points[shown]!.value)} r={4} />
            </>
          )}
        </svg>
      )}
      {shown !== null && points[shown] && (
        <div
          className="chart-tip"
          style={{
            left: Math.min(Math.max(x(shown), 70), width - 70),
            top: Math.max(0, y(points[shown]!.value) - 52),
          }}
        >
          <span>{label(points[shown]!.date)}</span>
          <b className="mono">{format(points[shown]!.value)}</b>
        </div>
      )}
    </div>
  );
}

/* ---- Horizontal bars ----------------------------------------------------------------------- */

export interface BarRow {
  id: string;
  label: string;
  value: number;
  /** The one row the reader is looking for (themselves, the top agent). */
  emphasis?: boolean;
  sub?: string;
}

/** A ranking: one bar per row from a shared baseline, the value at its tip. */
export function BarList({
  rows,
  format,
  max: forcedMax,
}: {
  rows: BarRow[];
  format: (n: number) => string;
  max?: number;
}) {
  const max = forcedMax ?? Math.max(0, ...rows.map((r) => r.value));
  return (
    <ol className="bars">
      {rows.map((r) => (
        <li key={r.id} className={r.emphasis ? "is-emphasis" : undefined}>
          <span className="bars-label truncate" title={r.label}>
            {r.label}
          </span>
          <span
            className="bars-track"
            title={`${r.label}: ${format(r.value)}${r.sub ? `, ${r.sub}` : ""}`}
          >
            <span
              className="bars-bar"
              style={{ "--share": max ? r.value / max : 0 } as React.CSSProperties}
            />
            <span className="bars-value mono">{format(r.value)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/* ---- Donut --------------------------------------------------------------------------------- */

/**
 * Share of a whole, at a glance (Salesforce's "Who is Contributing The Most?"). One slice in the
 * accent, the rest neutral, separated by the surface; the numbers are in the table beside it.
 */
export function Donut({
  slices,
  centre,
  centreLabel,
  size = 168,
}: {
  slices: BarRow[];
  centre: string;
  centreLabel: string;
  size?: number;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const total = slices.reduce((s, x) => s + x.value, 0);
  const r = size / 2 - 10;
  const inner = r - 22;
  const arcs = useMemo(
    () =>
      slices.reduce<(BarRow & { start: number; end: number })[]>((out, s) => {
        const start = out.length ? out[out.length - 1]!.end : -Math.PI / 2;
        const sweep = total ? (s.value / total) * Math.PI * 2 : 0;
        return [...out, { ...s, start, end: start + sweep }];
      }, []),
    [slices, total],
  );
  const c = size / 2;
  const pt = (rad: number, ang: number) =>
    `${(c + rad * Math.cos(ang)).toFixed(2)},${(c + rad * Math.sin(ang)).toFixed(2)}`;
  const focus = arcs.find((x) => x.id === hover);
  return (
    <div className="donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} role="img" aria-label={`${centreLabel}: ${centre}`}>
        {arcs.length === 1 && total > 0 ? (
          <circle
            cx={c}
            cy={c}
            r={(r + inner) / 2}
            className="donut-arc is-emphasis"
            strokeWidth={r - inner}
            fill="none"
          />
        ) : (
          arcs.map((s) => {
            if (s.end - s.start <= 0) return null;
            const large = s.end - s.start > Math.PI ? 1 : 0;
            const d = `M${pt(r, s.start)}A${r},${r} 0 ${large} 1 ${pt(r, s.end)}L${pt(inner, s.end)}A${inner},${inner} 0 ${large} 0 ${pt(inner, s.start)}Z`;
            return (
              <path
                key={s.id}
                d={d}
                className={`donut-slice${s.emphasis ? " is-emphasis" : ""}${hover === s.id ? " is-hover" : ""}`}
                onMouseEnter={() => setHover(s.id)}
                onMouseLeave={() => setHover(null)}
              >
                <title>{`${s.label}: ${total ? percent(s.value / total) : "0%"}`}</title>
              </path>
            );
          })
        )}
      </svg>
      <div className="donut-centre">
        {focus ? (
          <>
            <b className="truncate">{focus.label}</b>
            <span>{total ? percent(focus.value / total) : "–"}</span>
          </>
        ) : (
          <>
            <b>{centre}</b>
            <span>{centreLabel}</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Figures ------------------------------------------------------------------------------- */

export type Standing = "good" | "warning" | "bad";
const STANDING_WORD: Record<Standing, string> = {
  good: "On track",
  warning: "Close",
  bad: "Behind",
};

/** A number with its label; `standing` adds a worded mark, never colour alone. */
export function Stat({
  label,
  value,
  sub,
  standing,
  standingWord,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  standing?: Standing;
  standingWord?: string;
}) {
  return (
    <div className="stat">
      <span className="eyebrow">{label}</span>
      <div className="v">{value}</div>
      {(sub || standing) && (
        <div className="s">
          {standing && (
            <span
              className={`mark mark-${standing === "good" ? "accent" : standing === "warning" ? "ochre" : "brick"}`}
            >
              {standingWord ?? STANDING_WORD[standing]}
            </span>
          )}
          {sub}
        </div>
      )}
    </div>
  );
}

/**
 * Salesforce's gauge, as a meter: the count against a scale in three bands. The fill takes the
 * band's colour and the word says which band, so the reading never rests on colour. While the
 * week is still running (`elapsed` given), a low count is judged against the pace, in ochre at
 * worst: brick is for a week that is over and fell short.
 */
export function Meter({
  value,
  max,
  label,
  elapsed,
}: {
  value: number;
  max: number;
  label: string;
  elapsed?: number;
}) {
  const share = Math.min(1, max ? value / max : 0);
  let band: Standing = share >= 5 / 7 ? "good" : share >= 3 / 7 ? "warning" : "bad";
  let word = band === "good" ? "Strong week" : band === "warning" ? "Getting there" : "Slow week";
  let tone = band === "good" ? "accent" : band === "warning" ? "ochre" : "brick";
  if (elapsed !== undefined && band !== "good" && elapsed < 1) {
    const onPace = share >= elapsed * (5 / 7) - 0.02;
    band = onPace ? "warning" : "bad";
    word = onPace ? "On pace" : "Behind pace";
    tone = onPace ? "ink" : "ochre";
  }
  return (
    <div className={`meter meter-${band}${elapsed !== undefined ? " is-open" : ""}`}>
      <div className="meter-head">
        <span className="meter-value">{value}</span>
        <span className="t3">of {max}</span>
        <span className={`mark mark-${tone}`}>{word}</span>
      </div>
      <div
        className="meter-track"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={label}
      >
        <div className="meter-fill" style={{ width: `${share * 100}%` }} />
        {[3 / 7, 5 / 7].map((b) => (
          <span key={b} className="meter-band" style={{ left: `${b * 100}%` }} />
        ))}
      </div>
    </div>
  );
}

/* ---- The card every chart sits in ---------------------------------------------------------- */

export interface TableTwin {
  columns: string[];
  rows: (string | number)[][];
}

export function ChartCard({
  title,
  sub,
  table,
  children,
  actions,
}: {
  title: string;
  sub?: string;
  /** The same numbers as a table: the chart's accessible twin. */
  table?: TableTwin;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className="card chart-card">
      <div className="card-head">
        <div>
          <h3>{title}</h3>
          {sub && <p className="chart-sub">{sub}</p>}
        </div>
        <div className="chart-actions">
          {actions}
          {table && (
            <div className="seg seg-sm" role="tablist" aria-label={`${title}: view`}>
              <button role="tab" aria-selected={!asTable} onClick={() => setAsTable(false)}>
                Chart
              </button>
              <button role="tab" aria-selected={asTable} onClick={() => setAsTable(true)}>
                Table
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="card-body">
        {asTable && table ? (
          <div className="twin scroll">
            <table className="grid grid-compact">
              <thead>
                <tr>
                  {table.columns.map((c, i) => (
                    <th key={c} className={i ? "num" : undefined}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((cell, j) => (
                      <td key={j} className={j ? "num mono" : undefined}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
